import { TableComponent } from '@atoms';
import { useDebounce } from '@hooks';
import { Col, DatePicker, Input, Row, Select, Typography } from 'antd';
import { Dayjs } from 'dayjs';
import { memo, useCallback, useMemo, useState } from 'react';

import { useMarketplaceList } from '../hooks/useMarketplace';
import {
  MarketplaceItem,
  MarketplaceItemStatus,
  ProductType,
} from '../types/types';
import { formatDate } from '../utils/utils';
import { MarketplaceStatusTag } from './MarketplaceStatusTag';
import { MarketplaceTableActions } from './MarketplaceTableActions';

const { Option } = Select;
const { Text } = Typography;

export const ListMarketplaceTable = memo(() => {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    MarketplaceItemStatus | undefined
  >(undefined);
  const [typeFilter, setTypeFilter] = useState<ProductType>(
    ProductType.Program
  );

  const [dateRange, setDateRange] = useState<[string, string] | undefined>(
    undefined
  );

  const debouncedFilter = useDebounce(filter ?? '', 900);
  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);

  const { items, isLoading, total, refetch } = useMarketplaceList({
    type: typeFilter,
    page,
    limit: pageLimit,
    title: debouncedFilter || undefined,
    status: statusFilter,
    startDate: dateRange?.[0],
    endDate: dateRange?.[1],
  });

  const tableColumns = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 80,
        render: (id: number) => String(id).padStart(3, '0'),
      },
      {
        title: 'Item',
        dataIndex: 'title',
        sorter: (a: MarketplaceItem, b: MarketplaceItem) =>
          a.title.localeCompare(b.title),
      },
      {
        title: 'Data de Publicação',
        dataIndex: 'releaseDate',
        width: 160,
        render: (date: string | null) => (date ? formatDate(date) : '—'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 140,
        render: (status: MarketplaceItemStatus) => (
          <MarketplaceStatusTag status={status} />
        ),
      },
      {
        title: 'Ações',
        dataIndex: 'actions',
        width: 64,
        align: 'center' as const,
        render: (_: unknown, record: MarketplaceItem) => (
          <MarketplaceTableActions
            itemId={record.id}
            itemName={record.title}
            status={record.status}
            onActionComplete={refetch}
          />
        ),
      },
    ];
  }, [refetch]);

  const handleChangePage = useCallback(
    (selectedPage: number, paginationSize: number | undefined) => {
      setPage(selectedPage);
      if (paginationSize) setPageLimit(paginationSize);
    },
    []
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.value);
      setPage(1);
    },
    []
  );

  const handleStatusChange = useCallback((value: MarketplaceItemStatus) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleTypeChange = useCallback((value: ProductType) => {
    setTypeFilter(value);
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (dates && dates[0] && dates[1]) {
        setDateRange([
          dates[0].format('YYYY-MM-DD'),
          dates[1].format('YYYY-MM-DD'),
        ]);
      } else {
        setDateRange(undefined);
      }
      setPage(1);
    },
    []
  );

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col flex='auto'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Text type='secondary'>Buscar</Text>
            <Input.Search
              value={filter}
              onChange={handleInputChange}
              placeholder='Pesquisar...'
              style={{ width: '100%' }}
              allowClear
            />
          </div>
        </Col>
        <Col>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Text type='secondary'>Tipo do item</Text>
            <Select
              value={typeFilter}
              onChange={handleTypeChange}
              style={{ width: 140 }}
            >
              <Option value={ProductType.Program}>Programa</Option>
              <Option value={ProductType.Combo}>Combo</Option>
            </Select>
          </div>
        </Col>
        <Col>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Text type='secondary'>Data de publicação</Text>
            <DatePicker.RangePicker
              format='DD/MM/YYYY'
              placeholder={['Data de início', 'Data final']}
              onChange={handleDateRangeChange}
              style={{ width: '100%' }}
            />
          </div>
        </Col>
        <Col>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Text type='secondary'>Status</Text>
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              placeholder='Status'
              style={{ width: 140 }}
              allowClear
            >
              <Option value={MarketplaceItemStatus.Active}>Ativa</Option>
              <Option value={MarketplaceItemStatus.Draft}>Rascunho</Option>
              <Option value={MarketplaceItemStatus.Scheduled}>Agendada</Option>
              <Option value={MarketplaceItemStatus.Unpublished}>
                Despublicado
              </Option>
            </Select>
          </div>
        </Col>
      </Row>

      <TableComponent
        pageSize={pageLimit}
        columns={tableColumns}
        data={items}
        total={total}
        loading={isLoading}
        currentPage={page}
        onChangePage={(selectedPage, paginationPageSize) =>
          handleChangePage(selectedPage, paginationPageSize)
        }
        hidePaginationOnSinglePage={false}
        size='default'
      />
    </>
  );
});
